import React from "react";
import { Route, Switch, BrowserRouter as Router } from "react-router-dom";
import { IntlProvider } from "react-intl";
import Profile from "./profile/profile-routes";
import Participant from "./participant/routes";
import { ContactRoute as Contact, ContactMeetRoute as ContactMeet } from "./contact/routes";
import Corporation from "./corporation/routes";
import Errors from "./core/errors/errors-routes";
import { ApolloProvider } from "@apollo/client";
import { client } from "./core/api/client";
import Footer from "./core/footer";
import * as Storage from "./core/storage";
import ErrorModal from "./core/errors/error-modal";
import Header from "./core/header";
import { MuiThemeProvider, Dialog, CssBaseline, Box } from "@material-ui/core";
import { theme } from "./core/theme/theme";
import { Locale } from "./core/api/model";
type IntlProviderProps = React.ComponentProps<typeof IntlProvider>;

//@ts-ignore
const routes = [Profile, Participant, Contact, ContactMeet].concat(Object.values(Corporation)).concat([Errors]);

// INTERNATIONALISATION
const STORAGE_KEY_LOCALE = "locale";

function loadLocaleData(locale: Locale) {
  switch (locale) {
    case Locale.DE:
      return import("./core/lang/compiled/de.json");
    default:
      return import("./core/lang/compiled/en.json");
  }
}

export interface ErrorModal {
  active: boolean;
  title: string;
  description: string;
  raw: any;
}

interface Props {
  locale: Locale;
  messages: IntlProviderProps["messages"];
}

const App: React.FC<Props> = (props) => {
  const [errorModal, setErrorModal] = React.useState<ErrorModal>({
    active: false,
    title: "",
    description: "",
    raw: undefined,
  });
  const showErrorModal = (title: string, description: string, raw?: any) => {
    setErrorModal({ active: true, title, description, raw });
  };

  const [locale] = React.useState<Locale>(props.locale);
  const changeLocale = (locale: Locale) => {
    Storage.setStringItem(STORAGE_KEY_LOCALE, locale).then(() =>
      window.location.reload(false)
    );
  };

  return (
    <ApolloProvider client={client}>
      <IntlProvider
        locale={props.locale.split(/[-_]/)[0]}
        defaultLocale="en"
        messages={props.messages}
      >
        <MuiThemeProvider theme={theme}>
          <CssBaseline />

          <Router>
            <Header locale={locale} showErrorModal={showErrorModal} />
            <Box component="main">
              <Switch>
                {routes.map(({ component: Component, url, exact }) => {
                  return (
                    <Route
                      key={url}
                      path={url}
                      exact={exact}
                      render={(compProps) => (
                        <Component
                          {...compProps}
                          locale={locale}
                          showErrorModal={showErrorModal}
                        />
                      )}
                    />
                  );
                })}
              </Switch>
            </Box>
          </Router>

          <Footer locale={locale} changeLocale={changeLocale} />
          <Dialog
            open={errorModal.active}
            onClose={() => window.location.reload(false)} // reload page on lose error modal
            aria-labelledby="error-modal-title"
            aria-describedby="error-modal-description"
          >
            {ErrorModal(errorModal.title, errorModal.description)}
          </Dialog>
        </MuiThemeProvider>
      </IntlProvider>
    </ApolloProvider>
  );
};

export async function bootstrapApplication(_locale: string) {
  const localeStorage: string = await Storage.getStringItem(STORAGE_KEY_LOCALE);
  const locale = localeStorage ? (localeStorage as Locale) : Locale.EN;
  const messages = await loadLocaleData(locale);
  return <App locale={locale} messages={messages.default} />;
}
